/**
 * Admin Access Control Utilities
 * Provides consistent admin role checking across the application
 * 
 * IMPORTANT DISTINCTION:
 * 1. System Admin (userType: 'admin') - Platform-wide super admin, can see everything
 * 2. Agency Owner (userType: 'agency_owner') - Owns specific agencies, global user type
 * 3. Agency Admin (role: 'admin' within agency) - Admin within a specific agency only
 * 4. Regular Users (userType: 'agent', 'trial', 'viewer', etc.) - Standard users
 */

export type UserType = 'admin' | 'agency_owner' | 'agent' | 'trial' | 'viewer';
export type AgencyRole = 'owner' | 'admin' | 'manager' | 'agent' | 'viewer';

export interface AdminAccessLevel {
  isSystemAdmin: boolean;          // Platform-wide admin (userType: 'admin')
  isAgencyOwner: boolean;          // Global agency owner (userType: 'agency_owner')
  isAgencyAdmin: boolean;          // Admin within current agency (role: 'admin')
  hasSystemAccess: boolean;        // Can access system-wide features (admin-panel)
  hasSystemAdminPanel: boolean;    // Can access /admin-panel (system admins ONLY)
  hasAgencyManagement: boolean;    // Can manage agency settings and users (agency level)
  hasAgencyAccess: boolean;        // Can access agency-specific features
  canManageAllAgencies: boolean;   // Can see and manage all agencies (system admin only)
  canManageOwnAgencies: boolean;   // Can manage their own agencies
  canViewAnalytics: boolean;       // Can view analytics appropriate to their level
}

/**
 * Determines admin access levels for a given user type and agency role
 *
 * CRITICAL ACCESS RULES:
 * - /admin-panel: ONLY for system admins (userType: 'admin')
 * - /agency-dashboard: For agency owners and agency admins
 * - Agency owners should NEVER access admin-panel
 */
export const getAdminAccess = (
  userType: string,
  agencyRole?: string
): AdminAccessLevel => {
  const isSystemAdmin = userType === 'admin';
  const isAgencyOwner = userType === 'agency_owner';
  // Check for both 'owner' and 'agency_owner' since backend may use either
  const isAgencyAdmin = agencyRole === 'admin' || agencyRole === 'owner' || agencyRole === 'agency_owner';

  return {
    isSystemAdmin,
    isAgencyOwner,
    isAgencyAdmin,
    hasSystemAccess: isSystemAdmin,
    hasSystemAdminPanel: isSystemAdmin, // ONLY system admins can access /admin-panel
    hasAgencyManagement: isAgencyOwner || isAgencyAdmin, // Agency-level management (NOT system admin panel)
    hasAgencyAccess: isSystemAdmin || isAgencyOwner || isAgencyAdmin,
    canManageAllAgencies: isSystemAdmin, // Only system admin can see all agencies
    canManageOwnAgencies: isSystemAdmin || isAgencyOwner || isAgencyAdmin,
    canViewAnalytics: isSystemAdmin || isAgencyOwner || isAgencyAdmin,
  };
};

/**
 * Quick helper to check if user has any admin privileges
 * Considers both system admin and agency-level admin roles
 */
export const isAnyAdmin = (userType: string, agencyRole?: string): boolean => {
  return userType === 'admin' || 
         userType === 'agency_owner' || 
         agencyRole === 'admin' || 
         agencyRole === 'owner' ||
         agencyRole === 'agency_owner'; // Backend may use 'agency_owner' instead of 'owner'
};

/**
 * Check if user is system admin (full platform access)
 */
export const isSystemAdmin = (userType: string): boolean => {
  return userType === 'admin';
};

/**
 * Check if user is agency owner (global agency owner user type)
 */
export const isAgencyOwner = (userType: string): boolean => {
  return userType === 'agency_owner';
};

/**
 * Check if user has admin role within current agency
 */
export const isAgencyAdmin = (agencyRole?: string): boolean => {
  return agencyRole === 'admin' || agencyRole === 'owner';
};

/**
 * Get appropriate AI context based on user type and agency role
 */
export const getAIContextForUser = (userType: string, agencyRole?: string): 'admin_analytics' | 'agency_admin' | 'general' => {
  if (userType === 'admin') return 'admin_analytics'; // System admin gets full analytics
  if (userType === 'agency_owner' || agencyRole === 'admin' || agencyRole === 'owner') {
    return 'agency_admin'; // Agency-level admins get agency analytics
  }
  return 'general';
};

/**
 * Get appropriate navigation path for admin features
 */
export const getAdminNavigationPath = (userType: string, agencyRole?: string): string => {
  if (userType === 'admin') return '/admin-panel'; // System admin goes to admin panel
  if (userType === 'agency_owner' || agencyRole === 'admin' || agencyRole === 'owner') {
    return '/agency-dashboard'; // Agency admins go to agency management
  }
  return '/dashboard';
};

/**
 * Get admin feature label for UI display
 */
export const getAdminFeatureLabel = (userType: string, agencyRole?: string): string => {
  if (userType === 'admin') return 'System Admin Panel';
  if (userType === 'agency_owner') return 'Agency Owner Dashboard';
  if (agencyRole === 'admin') return 'Agency Admin Panel';
  if (agencyRole === 'owner') return 'Agency Management';
  return 'Dashboard';
};

/**
 * Check if user can see all agencies (system admin only)
 */
export const canSeeAllAgencies = (userType: string): boolean => {
  return userType === 'admin';
};

/**
 * Check if user can manage users (system admin, agency owner, or agency admin)
 */
export const canManageUsers = (userType: string, agencyRole?: string): boolean => {
  return userType === 'admin' || 
         userType === 'agency_owner' || 
         agencyRole === 'admin' || 
         agencyRole === 'owner';
};